import { Nav, INavLinkGroup, ThemeProvider, IconButton } from '@fluentui/react';
import type { IRenderGroupHeaderProps } from '@fluentui/react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Theme } from '../../../theme/Theme';
import styles from './VerticalNavigation.module.css';

interface IVerticalPageNavigationProps {
    expanded: boolean;
    visible?: boolean;
    onToggleExpand: (expand: boolean) => void;
    onToggleVisibility: (visible: boolean) => void;
}

const NAV_GROUPS: INavLinkGroup[] = [
    {
        links: [
            { name: 'Workspaces', url: '/workspaces', key: 'workspaces', icon: 'FabricFolder' },
            { name: 'Instances', url: '/', key: 'dashboard', icon: 'ViewDashboard' },
            { name: 'New Instance', url: '/spawn', key: 'spawn', icon: 'Add' },
            { name: 'Local Run', url: '/local', key: 'local', icon: 'Play' },
        ],
    },
];

function renderGroupHeader(props?: IRenderGroupHeaderProps, expanded?: boolean) {
    if (!props?.name) return null;
    if (!expanded) return <div className={styles.groupDivider} />;
    return (
        <div className={styles.groupHeader}>
            {props.name}
        </div>
    );
}

export default function VerticalPageNavigation({ expanded, visible, onToggleExpand, onToggleVisibility }: IVerticalPageNavigationProps) {
    const theme = Theme.get().navigation;
    const navigate = useNavigate();
    const location = useLocation();

    const getSelectedKey = (): string => {
        const path = location.pathname;
        if (path === '/spawn') return 'spawn';
        if (path === '/local') return 'local';
        if (path.startsWith('/workspaces')) return 'workspaces';
        if (path === '/' || path.startsWith('/instances')) return 'dashboard';
        return 'dashboard';
    };

    return (
        <ThemeProvider
            style={visible !== undefined ? {
                // @ts-ignore
                '--display': visible ? 'flex' : 'none',
            } : undefined}
            data-expanded={expanded}
            className={`${styles.root} agentbox__navigation--vertical`}
            theme={theme}
        >
            <Nav
                selectedKey={getSelectedKey()}
                groups={NAV_GROUPS}
                onRenderGroupHeader={(props) => renderGroupHeader(props, expanded)}
                styles={{
                    root: { width: '100%' },
                    groupContent: {
                        marginBottom: 0,
                    },
                    navItem: {
                        padding: 0,
                    },
                    link: {
                        height: 36,
                        lineHeight: '36px',
                        paddingLeft: expanded ? 12 : 0,
                        paddingRight: expanded ? undefined : 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: expanded ? 'flex-start' : 'center',
                        selectors: {
                            '&::after': {
                                borderLeft: 'none',
                            },
                            '.ms-Nav-icon': expanded ? {
                                fontSize: 16,
                            } : {
                                margin: '0',
                                fontSize: 16,
                                width: 48,
                                textAlign: 'center' as const,
                            },
                        },
                    },
                    compositeLink: {
                        selectors: {
                            '&.is-selected .ms-Nav-link': {
                                backgroundColor: 'transparent',
                                borderLeft: '3px solid var(--ab-accent)',
                                fontWeight: 600,
                            },
                            '&.is-selected .ms-Nav-link::after': {
                                borderLeft: 'none',
                            },
                            ...(expanded ? {} : {
                                '.ms-Nav-linkText': { display: 'none' },
                            }),
                        },
                    },
                    chevronButton: { display: 'none' },
                }}
                onLinkClick={(ev, item) => {
                    ev?.preventDefault();
                    if (item?.url) {
                        navigate(item.url);
                        onToggleVisibility(false);
                    }
                }}
            />
            <div className={styles.footerWrapper}>
                <IconButton
                    iconProps={{
                        iconName: expanded ? 'DoubleChevronLeft' : 'DoubleChevronRight',
                    }}
                    className={styles.expandCollapseButton}
                    styles={{
                        icon: { fontSize: 12 },
                        root: { width: 32, height: 32 },
                    }}
                    onClick={() => onToggleExpand(!expanded)}
                    ariaLabel={expanded ? 'Collapse navigation' : 'Expand navigation'}
                />
            </div>
        </ThemeProvider>
    );
}
